/* eslint-disable no-console */
/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { useDispatch } from "react-redux";
import { Button, Divider, Empty, List, Skeleton } from "antd";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { FilterOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { HotelsFilters } from "../../molecules/Hotel/HotelsFilters";
import { useFirestoreCollection } from "../../../hooks/useFirestoreCollection";
import {
  fetchCity83431FromFirestore,
  getRoomPricesWithDetails,
} from "../../../firestore/firestoreService";
import { IHotel28355 } from "../../../common/types";
import { HotelItem, HotelRequestForm } from "../../molecules";
import { HotelSearchBox } from "../../molecules/HotelSearchBox/HotelSearchBox";
import { urlStringToOBject } from "../../../common/util/util";
import {
  hotelBirthdayPromoConfig,
  isHotelBirthdayPromoActive,
} from "../../../common/util/hotelBirthdayPromo";
import { Dropdown, Select } from "../../atoms";
import { HotelWrapper } from "./Hotel.style";
import {
  buildDateSummary,
  buildGuestsSummary,
  buildProcessedHotels,
  filterHotelsByParams,
  filterAvailableHotels,
  orderHotelsForDisplay,
  promoteSelectedHotel,
} from "./hotelListHelpers";
const partnerId = process.env.REACT_APP_SITE_ID || "";
const sortOptions = [
  {
    label: defineMessage({
      description: "Alphabetically",
      defaultMessage: "Alphabetically",
      id: "sort-alpha",
    }),
    value: "alpha",
  },
  {
    label: defineMessage({
      description: "Price ascending",
      defaultMessage: "Price (ascending)",
      id: "sort-price-asc",
    }),
    value: "price-asc",
  },
  {
    label: defineMessage({
      description: "Price descending",
      defaultMessage: "Price (descending)",
      id: "sort-price-desc",
    }),
    value: "price-desc",
  },
];
export const HotelList: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const showPoupasPromoBanner = isHotelBirthdayPromoActive({ partnerId });
  const [_showRequestForm] = React.useState<boolean>(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const params = urlStringToOBject(location.search);
  const startDateParam = queryParams.get("startDate");
  const endDateParam = queryParams.get("endDate");
  const { nights, dateSummary } = React.useMemo(
    () => buildDateSummary(startDateParam, endDateParam),
    [startDateParam, endDateParam],
  );
  const [showFilter, setShowFilters] = React.useState(false);
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1200px)" });
  const [sorting, setSorting] = React.useState<string>("price-asc");
  const [cities, setCities] = React.useState([]);
  const isMobile = useMediaQuery({ query: "(max-width: 600px)" });
  const [baseHotels] = React.useState<IHotel28355[]>([]);
  const [currentHotels, setCurrentHotels] = React.useState<IHotel28355[]>([]);
  const [selectedHotelDetails] = React.useState<IHotel28355 | null>(null);
  const [loading] = React.useState<boolean>(false);
  const [priceLoading, setPriceLoading] = React.useState<boolean>(false);
  useFirestoreCollection({
    query: () =>
      fetchCity83431FromFirestore([
        {
          type: "filter",
          name: "countryCode",
          opr: "==",
          value: "CY",
        },
      ]),
    data: (data: any) =>
      setCities(data.map((v: any) => ({ value: v.code, label: v.name }))),
    local: true,
    deps: [dispatch],
  });
  const handelShowFilter = () => {
    setShowFilters((v) => !v);
  };
  const handelSortingChange = (value: string) => {
    setSorting(value);
  };
  const adultDefault = +(queryParams.get("adultNumbers") || 2);
  const childrenDefault = +(queryParams.get("childrenNumber") || 0);
  const infantDefault = +(queryParams.get("infant") || 0);
  const cityDefault = queryParams.get("city") || "all";
  const roomsDefault = +(queryParams.get("rooms") || 1);
  const guestsSummary = buildGuestsSummary(
    roomsDefault,
    adultDefault,
    childrenDefault,
  );
  // const dateFormat = "YYYY-MM-DD";
  const [hotelsWithRooms, setHotelsWithRooms] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (!startDateParam || !endDateParam || nights <= 0) {
      setCurrentHotels(baseHotels);
      setHotelsWithRooms([]);
      return;
    }
    // if (!baseHotels.length) return;
    let active = true;
    const loadPrices = async () => {
      setPriceLoading(true);
      try {
        const hotelsWithRoomsCurrent = await getRoomPricesWithDetails({
          fromDate: startDateParam,
          toDate: endDateParam,
          adults: adultDefault,
          children: childrenDefault,
          infants: infantDefault,
          partnerId,
        });
        setHotelsWithRooms(hotelsWithRoomsCurrent);
        setCurrentHotels(hotelsWithRoomsCurrent);
        // console.log({ hotelsWithRoomsCurrent });
      } catch (error) {
        console.error("Failed to load room prices", error);
      }
      if (!active) return;
      setPriceLoading(false);
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadPrices();
    return () => {
      active = false;
    };
  }, [
    startDateParam,
    endDateParam,
    // nights,
    // baseHotels,
    // computeNightPrice,
    cityDefault,
    adultDefault,
    childrenDefault,
    infantDefault,
    // travelDates,
  ]);
  const processedHotels = React.useMemo(
    () => buildProcessedHotels(hotelsWithRooms, currentHotels),
    [hotelsWithRooms, currentHotels],
  );
  const availableHotels = React.useMemo(
    () => filterAvailableHotels(processedHotels, hotelsWithRooms.length),
    [processedHotels, hotelsWithRooms.length],
  );
  const filteredHotels = React.useMemo(
    () => filterHotelsByParams(availableHotels, params),
    [availableHotels, params],
  );
  const requestedNights = nights > 0 ? nights : 0;
  const displayHotels = React.useMemo(() => {
    const ordered = orderHotelsForDisplay(
      filteredHotels,
      requestedNights,
      sorting,
    );
    return promoteSelectedHotel(ordered, params?.["hotel-id"]);
  }, [filteredHotels, requestedNights, sorting, params]);
  const selectedHotelId = params?.["hotel-id"];
  // console.log({ displayHotels });
  const sortingMenuItems = React.useMemo(
    () =>
      sortOptions.map((option) => ({
        key: option.value,
        label: formatMessage(option.label),
      })),
    [formatMessage],
  );
  return (
    <HotelWrapper>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`Poupas for holidays - Hotels list`}</title>
      </Helmet>
      <div className="hotel">
        <div
          className={classNames("hotel-body", {
            "filters-open": isTabletOrMobile && showFilter,
          })}
        >
          <div
            className={classNames("hotel-left", {
              hide: isTabletOrMobile ? !showFilter : showFilter,
            })}
          >
            <HotelsFilters
              showFilter={handelShowFilter}
              isTabletOrMobile={isTabletOrMobile}
              params={params}
              url={"hotels"}
              hotelCities={cities}
              hotels={availableHotels.map((h: any) => h.hotel)}
            />
          </div>

          <div className="hotel-main">
            {showPoupasPromoBanner && (
              <div className="hotel-promo-banner">
                <span>{hotelBirthdayPromoConfig.bannerText.prefix}</span>
                <span className="highlight">
                  {hotelBirthdayPromoConfig.bannerText.highlightDays}
                </span>
                <span> {hotelBirthdayPromoConfig.bannerText.dateRange}</span>
                <span>{hotelBirthdayPromoConfig.bannerText.middle}</span>
                <span className="highlight">
                  {hotelBirthdayPromoConfig.bannerText.highlightDiscount}
                </span>
                <span>{hotelBirthdayPromoConfig.bannerText.suffix}</span>
              </div>
            )}
            <div className="hotel-header">
              {isTabletOrMobile && (
                <div className="hotel-header-filter">
                  <Button type="link" onClick={handelShowFilter}>
                    <FilterOutlined />
                  </Button>
                </div>
              )}
              <div className="hotel-header-result">
                <span className="hotel-header-count">
                  {displayHotels?.length || 0} Αποτελέσματα
                </span>
                {dateSummary && !isMobile && (
                  <span className="hotel-header-dates">{dateSummary}</span>
                )}
              </div>
              <div className="hotel-header-sorting">
                <span className="title">
                  {!isMobile ? (
                    <FormattedMessage
                      description="Sorting"
                      defaultMessage="Sorting"
                      id="WwgJXV"
                    />
                  ) : (
                    <Dropdown
                      menu={{
                        items: sortingMenuItems,
                        onClick: ({ key }) => handelSortingChange(String(key)),
                      }}
                      trigger={["click"]}
                    >
                      <span role="button" tabIndex={0}>
                        <SortAscendingOutlined />
                      </span>
                    </Dropdown>
                  )}
                </span>
                {!isMobile && (
                  <Select
                    defaultValue="price-asc"
                    options={sortOptions.map((v) => ({
                      label: formatMessage(v.label),
                      value: v.value,
                    }))}
                    value={sorting}
                    onChange={handelSortingChange}
                  />
                )}
              </div>
            </div>
            <div className="hotel-searchbar">
              <HotelSearchBox
                cities={cities as any}
                adultNumbers={adultDefault}
                childrenNumber={childrenDefault}
                infant={infantDefault}
                city={cityDefault}
                startDate={startDateParam}
                endDate={endDateParam}
                hideSelectedCity
                hideCityField
                searchButtonLabel="Έλεγχος Διαθεσιμότητας"
              />
            </div>
            <div className="hotel-result hotel-list">
              <List
                rowKey={(item: any) =>
                  item?.isMinStay ? `${item.id}-min` : item.id
                }
                dataSource={displayHotels}
                loading={loading || priceLoading}
                renderItem={(item, index) => (
                  <React.Fragment>
                    <Skeleton loading={loading || priceLoading} active>
                      <HotelItem
                        hotelId={item.id}
                        hotel={item.hotel}
                        isMobile={isMobile}
                        filter={params}
                        guestParams={{
                          adultNumbers: adultDefault,
                          childrenNumber: childrenDefault,
                          infant: infantDefault,
                          rooms: roomsDefault,
                          childrenAges: queryParams.get("childrenAges") || "",
                        }}
                        priceSummary={{
                          totalPrice: item.displayPrice || 0,
                          primaryRoom: item?.primaryRoom,
                          sellingPrice: item?.primaryRoom?.sellingTotalPrice,
                          discountPrice: item?.primaryRoom?.discountPrice,
                          startDate: startDateParam,
                          endDate: endDateParam,
                          nights: requestedNights,
                          rooms: item?.primaryRoom?.prices || [],
                          periods: item?.primaryRoom?.periods || [],
                          minStay: item.minStay,
                          isMinStay: !!item.isMinStay,
                          onRequestType: item.onRequestType,
                          hasDirectAvailability: item.hasDirectAvailability,
                          guestsLabel: guestsSummary,
                          availableRoomsCount:
                            item?.primaryRoom?.availableRoomsCount || 0,
                          hasEarlyBookingDiscount:
                            item.primaryRoom?.hasEarlyBookingDiscount,
                        }}
                      />
                    </Skeleton>
                    {selectedHotelId &&
                      index === 0 &&
                      displayHotels.length > 1 && (
                        <Divider className="hotel-selected-divider">
                          Υπόλοιπα καταλύματα
                        </Divider>
                      )}
                    {selectedHotelDetails &&
                      index === 0 &&
                      currentHotels.length > 1 && (
                        <Divider className="hotel-similar-message">
                          Άλλα καταλύματα που ίσως σας ενδιαφέρουν
                        </Divider>
                      )}
                  </React.Fragment>
                )}
                locale={{
                  emptyText: (
                    <Empty description={"No Hotel available"}>
                      <HotelRequestForm />
                    </Empty>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </HotelWrapper>
  );
};
